import { useEffect, useRef, type RefObject } from 'react';
import mapboxgl from 'mapbox-gl';
import type { BrigadeStation, CoverageBorderFeatureCollection, CoverageFeatureCollection, LngLat } from '@duty/shared';
import { haversineMeters } from '@duty/shared';
import { MAPBOX_TOKEN, MAPBOX_STYLES } from '@/config/mapbox';
import type { ThemeName } from '@/theme/theme';

const GRID_SOURCE_ID = 'coverage-grid';
const GRID_BORDER_SOURCE_ID = 'coverage-grid-borders';
const STATION_SOURCE_ID = 'coverage-stations';

export const COVERAGE_PALETTE = ['#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#ec4899', '#14b8a6', '#ef4444', '#84cc16', '#0ea5e9', '#f97316'];

/** Stations closer than this are treated as visually adjacent — they must not share a colour. */
const ADJACENCY_METERS = 20_000;

/**
 * Session-lifetime colour memory: once a station is assigned a colour it
 * keeps it forever (until page reload), even if it later drops out of the
 * current viewport/candidate set. Without this, `assignStationColors` was
 * recomputed from scratch against whatever the *current* (viewport-driven,
 * constantly changing) candidate list happened to be — so the same station
 * could get a different colour on every pan, and the pin, the grid fill and
 * the sidebar swatch could transiently disagree with each other. Also keeps
 * every ever-seen station's coordinate around so a *new* station's colour is
 * chosen against the full history of nearby brigades, not just whoever is
 * currently on screen.
 */
const persistentStationById = new Map<string, BrigadeStation>();
const persistentStationColors = new Map<string, string>();

/**
 * Greedy map-colouring, append-only: assign the first palette colour not
 * already used by any *known* station (current or previously seen) within
 * `ADJACENCY_METERS`, then remember it for the rest of the session — a
 * station's colour is never reassigned once picked. Distance-aware (not
 * index-cycling), so two geographically neighbouring brigades never render
 * in the same colour just because they landed at indices that happen to
 * collide mod palette length.
 */
export function assignStationColors(stations: readonly BrigadeStation[]): Map<string, string> {
  for (const s of stations) persistentStationById.set(s.id, s);
  const newOnes = stations
    .filter((s) => !persistentStationColors.has(s.id))
    .sort((a, b) => a.id.localeCompare(b.id));
  for (const s of newOnes) {
    const usedNearby = new Set<string>();
    for (const [otherId, color] of persistentStationColors) {
      if (otherId === s.id) continue;
      const other = persistentStationById.get(otherId);
      if (!other) continue;
      if (haversineMeters(s.coord, other.coord) <= ADJACENCY_METERS) usedNearby.add(color);
    }
    const color =
      COVERAGE_PALETTE.find((c) => !usedNearby.has(c)) ??
      COVERAGE_PALETTE[persistentStationColors.size % COVERAGE_PALETTE.length]!;
    persistentStationColors.set(s.id, color);
  }
  return persistentStationColors;
}

/** Look up a station's assigned colour (falls back to grey for an unknown id). */
export function stationColor(stationId: string, colorMap: Map<string, string>): string {
  return colorMap.get(stationId) ?? '#9ca3af';
}

export interface UseCoverageMapParams {
  containerRef: RefObject<HTMLDivElement>;
  theme: ThemeName;
  center: LngLat;
  zoom?: number;
  /** Renders a red incident marker (omit for area-planning mode, which has no single point). */
  incidentMarker?: LngLat;
  stations: BrigadeStation[];
  grid: CoverageFeatureCollection | undefined;
  /** Region-boundary lines (preferred over the old per-cell grid line) — see coverageCellsToBorderGeoJSON. */
  gridBorders?: CoverageBorderFeatureCollection;
  /** When set, clicking anywhere on the map (not a station marker) reports the clicked point — e.g. to model an incident there. */
  onMapClick?: (point: LngLat) => void;
}

/**
 * Shared Mapbox setup for the full-screen coverage views: one fill layer for
 * the coverage grid (data-driven colour by station, per the "mobile fix" in
 * docs/response-coverage-2.0.md §3.3) plus one circle/label layer for
 * stations. Used by both the incident-scoped analysis view and the
 * no-incident coverage-planning view.
 */
export function useCoverageMap(params: UseCoverageMapParams): RefObject<mapboxgl.Map | null> {
  const mapRef = useRef<mapboxgl.Map | null>(null);

  // Kept current every render so the click listener (attached once, below)
  // always calls the latest callback rather than a stale one from mount time.
  const onMapClickRef = useRef(params.onMapClick);
  onMapClickRef.current = params.onMapClick;

  // Create the map once.
  useEffect(() => {
    if (!MAPBOX_TOKEN || !params.containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: params.containerRef.current,
      style: MAPBOX_STYLES[params.theme],
      center: [params.center.lng, params.center.lat],
      zoom: params.zoom ?? 10,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    if (params.incidentMarker) {
      new mapboxgl.Marker({ color: '#f45454' }).setLngLat([params.incidentMarker.lng, params.incidentMarker.lat]).addTo(map);
    }
    map.on('click', (e) => onMapClickRef.current?.({ lng: e.lngLat.lng, lat: e.lngLat.lat }));
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clickable cursor is the only visible sign the map takes clicks — swap it in/out as onMapClick is (un)set.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const canvas = map.getCanvas();
    canvas.style.cursor = params.onMapClick ? 'crosshair' : '';
  }, [params.onMapClick]);

  // Coverage fill + region-border layers — one source each, data-driven colour.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !params.grid) return;
    const colorMap = assignStationColors(params.stations);
    const colorExpr: unknown[] = ['match', ['get', 'stationId']];
    for (const s of params.stations) colorExpr.push(s.id, stationColor(s.id, colorMap));
    colorExpr.push('#9ca3af');

    const addLayer = () => {
      if (!map.getSource(GRID_SOURCE_ID)) {
        map.addSource(GRID_SOURCE_ID, { type: 'geojson', data: params.grid as unknown as GeoJSON.FeatureCollection });
        map.addLayer({
          id: `${GRID_SOURCE_ID}-fill`,
          type: 'fill',
          source: GRID_SOURCE_ID,
          paint: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'fill-color': colorExpr as any,
            // Deliberately light — a wash of colour to tell brigades apart, not a
            // blanket over the basemap; roads/trails underneath must stay readable.
            'fill-opacity': 0.2,
          },
        });
      } else {
        (map.getSource(GRID_SOURCE_ID) as mapboxgl.GeoJSONSource).setData(params.grid as unknown as GeoJSON.FeatureCollection);
      }

      if (params.gridBorders) {
        const borderData = params.gridBorders as unknown as GeoJSON.FeatureCollection;
        if (!map.getSource(GRID_BORDER_SOURCE_ID)) {
          map.addSource(GRID_BORDER_SOURCE_ID, { type: 'geojson', data: borderData });
          map.addLayer({
            id: `${GRID_BORDER_SOURCE_ID}-line`,
            type: 'line',
            source: GRID_BORDER_SOURCE_ID,
            paint: {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              'line-color': colorExpr as any,
              'line-width': 2,
              'line-opacity': 0.9,
            },
          });
        } else {
          (map.getSource(GRID_BORDER_SOURCE_ID) as mapboxgl.GeoJSONSource).setData(borderData);
        }
      }
    };
    if (map.isStyleLoaded()) addLayer();
    else map.once('load', addLayer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.grid, params.gridBorders, params.stations]);

  // Station markers, coloured to match the fill legend, labelled by name.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || params.stations.length === 0) return;
    const colorMap = assignStationColors(params.stations);
    const geojson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: params.stations.map((s) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [s.coord.lng, s.coord.lat] },
        properties: { id: s.id, name: s.name },
      })),
    };
    const addMarkers = () => {
      if (!map.getLayer(`${STATION_SOURCE_ID}-circles`)) {
        map.addSource(STATION_SOURCE_ID, { type: 'geojson', data: geojson });
        const colorExpr: unknown[] = ['match', ['get', 'id']];
        for (const s of params.stations) colorExpr.push(s.id, stationColor(s.id, colorMap));
        colorExpr.push('#9ca3af');
        map.addLayer({
          id: `${STATION_SOURCE_ID}-circles`,
          type: 'circle',
          source: STATION_SOURCE_ID,
          paint: {
            'circle-radius': 7,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'circle-color': colorExpr as any,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#0b0f14',
          },
        });
        map.addLayer({
          id: `${STATION_SOURCE_ID}-labels`,
          type: 'symbol',
          source: STATION_SOURCE_ID,
          layout: {
            'text-field': ['get', 'name'],
            'text-size': 11,
            'text-offset': [0, 1.4],
            'text-anchor': 'top',
          },
          paint: { 'text-color': '#e5e7eb', 'text-halo-color': '#0b0f14', 'text-halo-width': 1.2 },
        });
      } else {
        (map.getSource(STATION_SOURCE_ID) as mapboxgl.GeoJSONSource).setData(geojson);
      }
    };
    if (map.isStyleLoaded()) addMarkers();
    else map.once('load', addMarkers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.stations]);

  return mapRef;
}
